/**
 * Everything that talks to the server goes through here.
 *
 * credentials: 'include' on every call, because the session lives in an httpOnly cookie
 * that JavaScript cannot read. That is the point — a token in localStorage is readable
 * by any script that ends up on the page.
 */

async function call(path, { method = 'GET', body } = {}) {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body ? { 'content-type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });

  let payload = null;
  try { payload = await res.json(); } catch { /* empty body */ }

  if (!res.ok) {
    const err = new Error(payload?.error || `http_${res.status}`);
    err.status = res.status;
    err.code = payload?.error;
    err.detail = payload?.detail;
    throw err;
  }
  return payload;
}

export const api = {
  me: () => call('/api/auth/me'),
  login: (tenant, email, password) => call('/api/auth/login', { method: 'POST', body: { tenant, email, password } }),
  logout: () => call('/api/auth/logout', { method: 'POST' }),
  changePassword: (currentPassword, newPassword) =>
    call('/api/auth/password', { method: 'POST', body: { currentPassword, newPassword } }),
  incidents: () => call('/api/incidents'),
  incident: (id) => call(`/api/incidents/${id}`),
  newIncidentOptions: () => call('/api/incidents/options'),
  createIncident: (body) => call('/api/incidents', { method: 'POST', body }),
  updateIncident: (id, body) => call(`/api/incidents/${id}`, { method: 'PATCH', body }),
  setItemState: (incidentId, itemId, state) =>
    call(`/api/incidents/${incidentId}/items/${itemId}`, { method: 'PATCH', body: { state } }),
  addNote: (incidentId, message, positionId = null) =>
    call(`/api/incidents/${incidentId}/log`, { method: 'POST', body: { message, positionId } }),

  createAction: (incidentId, body) =>
    call(`/api/incidents/${incidentId}/actions`, { method: 'POST', body }),
  // Partial by design: send only what changed. Sending the whole object back would let a
  // stale form overwrite a field somebody else edited while it was open.
  updateAction: (incidentId, actionId, body) =>
    call(`/api/incidents/${incidentId}/actions/${actionId}`, { method: 'PATCH', body }),

  // Objectives, strategies and the tactic link. One GET returns both objectives (with
  // their rollup counts) and strategies (with their tactics), because the screen is
  // useless with only half of it and two requests would render it in two stages.
  objectives: (incidentId) => call(`/api/incidents/${incidentId}/objectives`),
  createObjective: (incidentId, body) =>
    call(`/api/incidents/${incidentId}/objectives`, { method: 'POST', body }),
  updateObjective: (incidentId, objectiveId, body) =>
    call(`/api/incidents/${incidentId}/objectives/${objectiveId}`, { method: 'PATCH', body }),
  createStrategy: (incidentId, objectiveId, body) =>
    call(`/api/incidents/${incidentId}/objectives/${objectiveId}/strategies`, { method: 'POST', body }),
  updateStrategy: (incidentId, strategyId, body) =>
    call(`/api/incidents/${incidentId}/strategies/${strategyId}`, { method: 'PATCH', body }),
  // strategyId null unlinks. The action itself is untouched either way.
  setTactic: (incidentId, actionId, strategyId) =>
    call(`/api/incidents/${incidentId}/actions/${actionId}/tactic`, { method: 'PATCH', body: { strategyId } }),

  // The plan library. One GET brings the whole library plus the position codes the editor
  // offers, because the editor needs both the moment it opens and a second round trip is a
  // dropdown that is briefly empty while somebody is looking at it.
  plans: () => call('/api/plans'),
  createPlan: (body) => call('/api/plans', { method: 'POST', body }),
  updatePlan: (planId, body) => call(`/api/plans/${planId}`, { method: 'PATCH', body }),
  deletePlan: (planId) => call(`/api/plans/${planId}`, { method: 'DELETE' }),
  // fromVersionId copies that version's items into the new draft. Passing null starts empty,
  // which is what you want for a plan's very first revision and almost never afterwards.
  startDraft: (planId, fromVersionId) =>
    call(`/api/plans/${planId}/versions`, { method: 'POST', body: { fromVersionId } }),

  planVersion: (versionId) => call(`/api/plans/versions/${versionId}`),
  addPlanItem: (versionId, body) =>
    call(`/api/plans/versions/${versionId}/items`, { method: 'POST', body }),
  // A pasted list. One request, one transaction — ten lines are ten rows or none, so a
  // half-applied paste can never leave somebody guessing which lines were dropped.
  addPlanItems: (versionId, body) =>
    call(`/api/plans/versions/${versionId}/items/bulk`, { method: 'POST', body }),
  updatePlanItem: (versionId, itemId, body) =>
    call(`/api/plans/versions/${versionId}/items/${itemId}`, { method: 'PATCH', body }),
  deletePlanItem: (versionId, itemId) =>
    call(`/api/plans/versions/${versionId}/items/${itemId}`, { method: 'DELETE' }),
  reorderPlanItems: (versionId, itemIds) =>
    call(`/api/plans/versions/${versionId}/reorder`, { method: 'POST', body: { itemIds } }),
  approvePlanVersion: (versionId) =>
    call(`/api/plans/versions/${versionId}/approve`, { method: 'POST' }),
  retirePlanVersion: (versionId) =>
    call(`/api/plans/versions/${versionId}/retire`, { method: 'POST' }),
  discardDraft: (versionId) => call(`/api/plans/versions/${versionId}`, { method: 'DELETE' }),

  // Layering a plan onto an incident that is already running.
  incidentPlans: (incidentId) => call(`/api/incidents/${incidentId}/plans`),
  applyPlan: (incidentId, planVersionId) =>
    call(`/api/incidents/${incidentId}/plans`, { method: 'POST', body: { planVersionId } }),

  // The site plan. Upload sends the File as the RAW body with its own content-type — no
  // multipart, so the server needs no parsing dependency and the body is exactly the bytes.
  // The caption rides in the query string.
  sitePlans: (incidentId) => call(`/api/incidents/${incidentId}/site-plans`),
  sitePlanUrl: (incidentId, planId) =>
    `/api/incidents/${incidentId}/site-plans/${planId}/file`,
  uploadSitePlan: async (incidentId, file, caption) => {
    const q = new URLSearchParams({ caption: caption || '', filename: file.name || '' });
    const res = await fetch(`/api/incidents/${incidentId}/site-plans?${q}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': file.type },
      body: file,
    });
    let payload = null;
    try { payload = await res.json(); } catch { /* empty body */ }
    if (!res.ok) {
      const err = new Error(payload?.error || `http_${res.status}`);
      err.status = res.status; err.code = payload?.error; err.detail = payload?.detail;
      throw err;
    }
    return payload;
  },
  withdrawSitePlan: (incidentId, planId) =>
    call(`/api/incidents/${incidentId}/site-plans/${planId}/withdraw`, { method: 'POST' }),

  users: () => call('/api/users'),
  createUser: (body) => call('/api/users', { method: 'POST', body }),
  updateUser: (id, body) => call(`/api/users/${id}`, { method: 'PATCH', body }),
  setUserPassword: (id, password) =>
    call(`/api/users/${id}/password`, { method: 'POST', body: { password } }),

  enquiries: () => call('/api/enquiries'),
  setEnquiryHandled: (id, handled) =>
    call(`/api/enquiries/${id}`, { method: 'PATCH', body: { handled } }),
  handoverBrief: (incidentId, positionId) =>
    call(`/api/incidents/${incidentId}/positions/${positionId}/handover`),
  // userId links the seat to an ACCOUNT. Omitting it records the watch under a typed name
  // and leaves position_assignments.user_id null, which is what "your own function" is read
  // from — so without it a section lead sits in a seat the server cannot see them in.
  handOver: (incidentId, positionId, holderName, notes, userId = null) =>
    call(`/api/incidents/${incidentId}/positions/${positionId}/handover`,
      { method: 'POST', body: { holderName, notes, userId } }),
};

/**
 * Live connection to one incident.
 *
 * Reconnects with a backoff. A control room laptop drops its wifi and the operator does
 * not know or care — the board must come back on its own, and it must resync rather than
 * assume the state it had before the drop is still true.
 */
export function connectIncident(incidentId, { onEvent, onStatus }) {
  let ws = null, attempt = 0, closed = false, timer = null;

  const open = () => {
    if (closed) return;
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    ws = new WebSocket(`${proto}://${location.host}/ws?incident=${encodeURIComponent(incidentId)}`);

    ws.onopen = () => {
      const wasDown = attempt > 0;
      attempt = 0;
      onStatus?.('live');
      // A deploy restarts the service, so every socket drops and comes back. That is the
      // earliest reliable signal that the code on the server may no longer be the code in
      // this browser, so it is worth telling anyone who is listening.
      if (wasDown) window.dispatchEvent(new Event('imtlogic:reconnected'));
    };
    ws.onmessage = (e) => {
      try { onEvent?.(JSON.parse(e.data)); } catch { /* ignore a malformed frame */ }
    };
    ws.onclose = () => {
      onStatus?.('offline');
      if (closed) return;
      // 1s, 2s, 4s … capped at 15s. An unbounded retry storm from fifty tabs is its own
      // outage, and a fixed 1s retry is the same thing more slowly.
      const wait = Math.min(1000 * 2 ** attempt++, 15000);
      timer = setTimeout(open, wait);
    };
    ws.onerror = () => ws.close();
  };

  open();
  return () => { closed = true; clearTimeout(timer); ws?.close(); };
}
